import { ReactElement, CSSProperties } from 'react';
import { Tooltip } from '@material-ui/core';
import * as MUI_ICONS from '@material-ui/icons'; // eslint-disable-line
import * as WITBOOST_ICONS from '../Icons';
import { DEFAULT_WITBOOST_ICONS } from './DefaultWitboostIcons';

export type WbIconDefinition = WbIconObjectProps | string;

export type WbIconProps = {
  icon: WbIconDefinition;
  style?: CSSProperties;
};

export type WbIconObjectProps = {
  value: string;
  color?: string;
  map?: Record<string, any>;
  tooltip?: string;
  style?: Record<string, any>;
};

type IconParser = (
  icon: WbIconObjectProps,
  style: CSSProperties,
) => ReactElement | undefined;

const normalizeIcon = (icon: WbIconDefinition): WbIconObjectProps => {
  if (typeof icon === 'string') return { value: icon };

  if (typeof icon === 'object') {
    const { value, color, map, tooltip, style } = icon;
    return {
      value: value || '',
      ...(color && { color }),
      ...(map && { map }),
      ...(tooltip && { tooltip }),
      ...(style && { style }),
    };
  }
  return icon;
};

const parseMuiIcon: IconParser = (
  iconObject: WbIconObjectProps,
  style: CSSProperties,
): ReactElement | undefined => {
  if ((iconObject.value || '').match(/^mui\:/)) {
    const Icon = (MUI_ICONS as Record<string, any>)[
      iconObject.value.replace(/^mui:/, '')
    ];
    if (Icon) {
      if ('color' in iconObject) style.color = iconObject.color;
      return <Icon style={style} {...iconObject} />;
    }
    return undefined;
  }
  return undefined;
};

const parseWitboostIcon: IconParser = (
  iconObject: WbIconObjectProps,
  style: CSSProperties,
): ReactElement | undefined => {
  if ((iconObject.value || '').match(/^internal\:/)) {
    const Icon = (WITBOOST_ICONS as Record<string, any>)[
      iconObject.value.replace(/^internal:/, '')
    ];
    if (Icon) {
      if ('color' in iconObject) style.color = iconObject.color;
      return <Icon style={style} />;
    }
    return undefined;
  }
  return undefined;
};

const parseLinkIcon: IconParser = (
  icon: WbIconObjectProps,
  style: CSSProperties,
): ReactElement | undefined => {
  if (
    !!(icon.value || '').match(/^external:https?\:/) ||
    !!(icon.value || '').match(/^external:\//)
  ) {
    const iconStyle = {
      width: '1em',
      height: '1em',
      fontSize: '1.7em',
      ...style,
    };
    return (
      <img
        alt=""
        style={iconStyle}
        src={icon.value.replace(/^external:/, '')}
        {...icon}
      />
    );
  }
  return undefined;
};

const parseDefaultWitboostIcon: IconParser = (
  icon: WbIconObjectProps,
  style: CSSProperties,
): ReactElement | undefined => {
  if (icon.value in DEFAULT_WITBOOST_ICONS) {
    return parseIcon(DEFAULT_WITBOOST_ICONS[icon.value], style);
  }
  return undefined;
};

function parseIcon(
  iconDefinition: WbIconDefinition,
  style: CSSProperties,
): ReactElement | undefined {
  const parsers: IconParser[] = [
    parseMuiIcon,
    parseLinkIcon,
    parseWitboostIcon,
    parseDefaultWitboostIcon,
  ];
  const icon = normalizeIcon(iconDefinition);
  return parsers.reduce((parsed, parser) => {
    if (parsed) return parsed;
    return parser(icon, style);
  }, undefined as ReactElement | undefined);
}

export const WbIcon = (props: WbIconProps) => {
  let iconProps = normalizeIcon(props.icon);
  // if iconProps.map is defined and the value of iconProps is a string
  // and the value of iconProps is in the map
  if (
    iconProps.map &&
    typeof iconProps.value === 'string' &&
    iconProps.value in iconProps.map
  ) {
    const typeOfIconValue = typeof iconProps.map[iconProps.value];
    // We have 2 types of map supported with 2 different structures
    // map (string) :
    //  key : icon
    //  key2 : icon

    // map (object) :
    //    key :
    //        value: icon
    //        color: color
    //    key2 :
    //        value: icon
    //        color: color
    // value: {{ prop }}

    if (typeOfIconValue === 'string') {
      // in this case we need to override only the value og icon with the value from the map
      iconProps = {
        ...iconProps,
        value: iconProps.map[iconProps.value],
      };
    } else if (typeOfIconValue === 'object') {
      // in this case we need to override value and others props with the value of the map
      iconProps = {
        ...iconProps,
        ...iconProps.map[iconProps.value],
      };
    }
  }
  const icon = parseIcon(iconProps, props.style ?? {});

  if (icon) {
    if (iconProps.tooltip) {
      return <Tooltip title={iconProps.tooltip}>{icon}</Tooltip>;
    }
    return icon;
  }
  return <></>;
};
